/**
 * CorporateEvolutionView — /corporate/monthly/trends
 * Monthly evolution trend + YTD + Trailing 12M + Target vs Actual.
 * IFRS S2 aligned intensity view for corporate sustainability reporting.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, TrendingDown, TrendingUp, Minus, Target, Building2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useCorporatePeriodic } from "@/hooks/useCorporatePeriodic";
import PortfolioIntelligenceTab from "@/components/PortfolioIntelligenceTab";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const DECISION_COLORS = {
  ready_for_management_review: "bg-emerald-100 text-emerald-700 border-emerald-200",
  ready_for_external_reporting: "bg-teal-100 text-teal-700 border-teal-200",
  proceed_with_conditions: "bg-amber-100 text-amber-700 border-amber-200",
  technical_assistance_required: "bg-orange-100 text-orange-700 border-orange-200",
  data_improvement_required: "bg-red-100 text-red-700 border-red-200",
  proceed_to_credit_committee: "bg-emerald-100 text-emerald-700 border-emerald-200",
  not_financeable: "bg-red-100 text-red-700 border-red-200",
};
function DecisionBadge({ decision }) {
  if (!decision?.outcome) return null;
  const cls = DECISION_COLORS[decision.outcome] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cls}`}>{decision.outcome_label}</span>
  );
}
const SEV_COLORS = { critical: "text-red-600 bg-red-50", high: "text-amber-600 bg-amber-50", medium: "text-orange-500 bg-orange-50", low: "text-slate-500 bg-slate-50" };

function TrendBar({ label, scope1, scope2, scope3, maxTotal, isLocked }) {
  const total = (scope1 || 0) + (scope2 || 0) + (scope3 || 0);
  const pct   = total / Math.max(maxTotal, 0.001);
  const s1w   = maxTotal > 0 ? (scope1 / maxTotal) * 100 : 0;
  const s2w   = maxTotal > 0 ? (scope2 / maxTotal) * 100 : 0;
  const s3w   = maxTotal > 0 ? (scope3 / maxTotal) * 100 : 0;

  return (
    <div className="flex items-end gap-1.5" style={{ height: 80 }}>
      <div className="flex-1 flex flex-col justify-end" title={`${label}: ${total.toFixed(3)} tCO2e`}>
        <div className="w-full flex flex-col rounded-t overflow-hidden" style={{ height: `${pct * 72}px` }}>
          <div className="bg-blue-400 flex-none" style={{ height: `${s3w * pct}%` }} />
          <div className="bg-teal-500 flex-none" style={{ height: `${s2w * pct}%` }} />
          <div className="bg-amber-500 flex-none" style={{ height: `${s1w * pct}%` }} />
        </div>
        <p className="text-xs text-slate-400 text-center mt-1 leading-tight">{label?.slice(5)}</p>
        {isLocked && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mx-auto" />}
      </div>
    </div>
  );
}

function DeltaBadge({ delta }) {
  if (!delta || delta.total_pct == null) return <span className="text-slate-300 text-xs">—</span>;
  const pct = delta.total_pct || 0;
  const up  = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${up ? "text-red-600" : pct < 0 ? "text-emerald-600" : "text-slate-400"}`}>
      {up ? <TrendingUp size={9} /> : pct < 0 ? <TrendingDown size={9} /> : <Minus size={9} />}
      {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

function IntensityRow({ label, value }) {
  if (value == null) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-50">
      <p className="text-xs text-slate-500">{label.replace(/_/g, " ")}</p>
      <p className="text-xs font-bold text-purple-700">{typeof value === "number" ? value.toFixed(4) : value}</p>
    </div>
  );
}

export default function CorporateEvolutionView({ defaultView = "trend" }) {
  const navigate = useNavigate();
  const { evolution, ytdData, trailing12, loading, orgName, selectedYear, setSelectedYear } = useCorporatePeriodic();
  const [view, setView] = useState(defaultView);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const maxTotal = Math.max(...evolution.map(p => p.total_tco2e || 0), 0.001);

  return (
    <div className="p-6 lg:p-8 space-y-6">
        <button onClick={() => navigate("/corporate")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          ← Corporate Workspace
        </button>

        <PageHeader
          label="CORPORATE WORKSPACE"
          title="GHG Emissions Trend"
          subtitle={`${orgName ? orgName + ' · ' : ''}Monthly evolution · YTD · Trailing 12M · IFRS S2 intensity · Target vs actual`}
          backUrl="/corporate"
          backLabel="Corporate Dashboard"
        />

        {/* View tabs */}
        <div className="flex gap-1 mb-5 p-1 bg-white border border-slate-200 rounded-xl overflow-x-auto">
          {[
            { id: "trend",     label: "Monthly Trend"    },
            { id: "ytd",       label: "Year to Date"     },
            { id: "t12",       label: "Trailing 12M"     },
            { id: "intensity", label: "IFRS S2 Intensity"},
            { id: "portfolio", label: "Portfolio Intel"  },
            { id: "decision",  label: "Decisions"        },
            { id: "anomalies", label: "Anomalies"        },
            { id: "target",    label: "Target vs Actual" },
          ].map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${view === v.id ? "bg-[#0F172A] text-white" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
              data-testid={`view-${v.id}`}>
              {v.label}
            </button>
          ))}
        </div>

        {evolution.length === 0 && (
          <SectionCard>
            <div className="py-10 text-center">
              <Target size={24} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-600">No monthly data yet</p>
              <p className="text-xs text-slate-400 mb-4">Enter your first month's GHG data to start tracking.</p>
              <CTAButton variant="action" onClick={() => navigate("/corporate/monthly/inputs")}>Enter Monthly Data →</CTAButton>
            </div>
          </SectionCard>
        )}

        {/* Monthly Trend */}
        {evolution.length > 0 && view === "trend" && (
          <div className="space-y-6">
            <SectionCard title="Monthly GHG Emissions (tCO₂e)" testid="trend-chart" action={
              <div className="flex gap-3 text-xs">
                {[["bg-amber-500","Scope 1"],["bg-teal-500","Scope 2"],["bg-blue-400","Scope 3"]].map(([c,l]) => (
                  <span key={l} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-sm ${c}`}/>{l}</span>
                ))}
              </div>
            }>
              <div className="flex items-end gap-1 min-h-[90px]">
                {evolution.map(p => (
                  <TrendBar key={p.period_label} label={p.period_label}
                    scope1={p.scope1_tco2e} scope2={p.scope2_tco2e} scope3={p.scope3_tco2e}
                    maxTotal={maxTotal} isLocked={p.is_locked} />
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Period Detail" noPadding>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  {["Period","Total","Scope 1","Scope 2","Scope 3","vs Prior","Status"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-500 px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {evolution.slice().reverse().map(p => (
                    <tr key={p.period_label} className="border-b border-slate-50 hover:bg-slate-50/50"
                      data-testid={`period-row-${p.period_label}`}>
                      <td className="px-4 py-2.5 font-bold text-slate-900">{p.period_label}</td>
                      <td className="px-4 py-2.5 font-bold text-teal-700">{(p.total_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-amber-600">{(p.scope1_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-teal-600">{(p.scope2_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-blue-600">{(p.scope3_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5"><DeltaBadge delta={p.delta_vs_prior} /></td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.is_locked ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {p.is_locked ? "Locked" : p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          </div>
        )}

        {/* YTD */}
        {view === "ytd" && ytdData && (
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
              <MetricCard label={`YTD Total ${selectedYear}`}  value={`${(ytdData.ytd_total_tco2e||0).toFixed(3)} tCO₂e`} color="text-teal-700" />
              <MetricCard label={`Scope 1 ${selectedYear}`}    value={(ytdData.ytd_scope1_tco2e||0).toFixed(3)}            color="text-amber-700" />
              <MetricCard label={`Scope 2 ${selectedYear}`}    value={(ytdData.ytd_scope2_tco2e||0).toFixed(3)}            color="text-teal-600" />
              <MetricCard label={`Scope 3 ${selectedYear}`}    value={(ytdData.ytd_scope3_tco2e||0).toFixed(3)}            color="text-blue-600" />
            </div>
            {ytdData.annual_target_tco2e && (
              <SectionCard title="Target Progress" testid="target-progress">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-600">Annual target: {ytdData.annual_target_tco2e} tCO₂e</p>
                  <p className="text-xs font-bold text-slate-700">{ytdData.target_pct_achieved}% used</p>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${
                    ytdData.target_pct_achieved > 90 ? "bg-red-500" :
                    ytdData.target_pct_achieved > 70 ? "bg-amber-500" : "bg-emerald-500"
                  }`} style={{ width: `${Math.min(ytdData.target_pct_achieved || 0, 100)}%` }} />
                </div>
                <p className="text-xs text-slate-400 mt-1">SBTi / NDC aligned target tracking</p>
              </SectionCard>
            )}
            <SectionCard title={`Monthly Breakdown — ${selectedYear}`} noPadding>
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  {["Month","Total","Scope 1","Scope 2","Scope 3","Cumulative YTD"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-500 px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(ytdData.monthly_breakdown || []).map((m, i) => {
                    const cumulative = (ytdData.monthly_breakdown || []).slice(0, i+1).reduce((s, x) => s + (x.total_tco2e||0), 0);
                    return (
                      <tr key={m.period_label} className="border-b border-slate-50">
                        <td className="px-4 py-2">{m.period_label}</td>
                        <td className="px-4 py-2 font-bold text-teal-700">{(m.total_tco2e||0).toFixed(3)}</td>
                        <td className="px-4 py-2 text-amber-600">{(m.scope1_tco2e||0).toFixed(3)}</td>
                        <td className="px-4 py-2 text-teal-600">{(m.scope2_tco2e||0).toFixed(3)}</td>
                        <td className="px-4 py-2 text-blue-600">{(m.scope3_tco2e||0).toFixed(3)}</td>
                        <td className="px-4 py-2 text-slate-600">{cumulative.toFixed(3)} tCO2e</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </SectionCard>
          </div>
        )}

        {/* Trailing 12M */}
        {view === "t12" && trailing12 && (
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4">
              <MetricCard label="T12M Total" value={`${(trailing12.t12_total_tco2e||0).toFixed(2)} tCO₂e`} color="text-teal-700" />
              <MetricCard label="Scope 1"    value={(trailing12.t12_scope1_tco2e||0).toFixed(2)}           color="text-amber-700" />
              <MetricCard label="Scope 2"    value={(trailing12.t12_scope2_tco2e||0).toFixed(2)}           color="text-teal-600" />
              <MetricCard label="Scope 3"    value={(trailing12.t12_scope3_tco2e||0).toFixed(2)}           color="text-blue-600" />
            </div>
            <p className="text-xs text-slate-400 italic text-center">
              Trailing 12 months ({trailing12.periods_included} periods). GHG Protocol boundary.
            </p>
          </div>
        )}

        {/* IFRS S2 Intensity */}
        {view === "intensity" && evolution.length > 0 && (
          <div className="space-y-6">
            <SectionCard
              title="IFRS S2 Intensity Metrics — Monthly"
              subtitle="Intensity = tCO₂e / business metric. Required under IFRS S2 Climate-related Disclosures."
              noPadding
              className="border-purple-200"
            >
              <table className="w-full text-xs">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  {["Period","Total tCO₂e","tCO₂e/FTE","tCO₂e/$M rev","tCO₂e/tonne"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-500 px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {evolution.slice().reverse().map(p => (
                    <tr key={p.period_label} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5 font-bold text-slate-900">{p.period_label}</td>
                      <td className="px-4 py-2.5 font-bold text-teal-700">{(p.total_tco2e||0).toFixed(3)}</td>
                      <td className="px-4 py-2.5 text-purple-700">{p.intensity?.tco2e_per_fte?.toFixed(4) || "—"}</td>
                      <td className="px-4 py-2.5 text-purple-700">{p.intensity?.tco2e_per_1musd_revenue?.toFixed(2) || "—"}</td>
                      <td className="px-4 py-2.5 text-purple-700">{p.intensity?.tco2e_per_tonne_product?.toFixed(4) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
            <p className="text-xs text-slate-400 italic">
              Intensity metrics require FTE headcount, revenue, or production volume inputs to populate.
              Not third-party verified — Platform-verified (internal).
            </p>
          </div>
        )}

        {view === "portfolio" && (
          <PortfolioIntelligenceTab workspace="corporate" mode="operational" />
        )}

        {/* Decision */}
        {view === "decision" && (
          <SectionCard title="Decision Intelligence — Per Period" noPadding testid="decision-view">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 border-b border-slate-100">
                  {["Period","Total tCO₂e","Recommendation","Confidence","Trajectory"].map(h => (
                    <th key={h} className="text-left text-xs text-slate-500 px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {evolution.slice().reverse().map(p => {
                    const dec = p.decision || {};
                    return (
                      <tr key={p.period_label} className="border-b border-slate-50 hover:bg-slate-50/40">
                        <td className="px-4 py-2.5 font-bold text-slate-900">{p.period_label}</td>
                        <td className="px-4 py-2.5 font-bold text-teal-700">{(p.total_tco2e||0).toFixed(3)}</td>
                        <td className="px-4 py-2.5"><DecisionBadge decision={dec} /></td>
                        <td className="px-4 py-2.5 text-xs font-bold text-slate-600">{dec.confidence_score ?? "—"}/100</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 capitalize">{(p.trajectory||"—").replace(/_/g," ")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </SectionCard>
        )}
        {view === "anomalies" && (
          <div className="space-y-4" data-testid="anomalies-view">
            {evolution.filter(p => (p.anomaly_flags || []).length > 0).length === 0 ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
                <CheckCircle2 size={24} className="text-emerald-400 mx-auto mb-3" />
                <p className="text-sm font-bold text-emerald-700">No anomalies detected</p>
                <p className="text-xs text-emerald-600">All periods within normal variance thresholds.</p>
              </div>
            ) : (
              evolution.slice().reverse().filter(p => (p.anomaly_flags || []).length > 0).map(p => (
                <div key={p.period_label} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-900">{p.period_label} — {(p.total_tco2e||0).toFixed(3)} tCO2e</p>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                      {(p.anomaly_flags||[]).length} flag{(p.anomaly_flags||[]).length > 1 ? "s" : ""}
                    </span>
                  </div>
                  {(p.anomaly_flags||[]).map((a, i) => {
                    const sc = SEV_COLORS[a.severity] || SEV_COLORS.low;
                    return (
                      <div key={i} className="px-4 py-2.5 border-b border-slate-50">
                        <div className="flex items-start gap-2">
                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded mt-0.5 ${sc}`}>{a.severity?.toUpperCase()}</span>
                          <div>
                            <p className="text-xs font-bold text-slate-700">{a.code}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{a.description}</p>
                            <p className="text-xs text-slate-400 italic mt-0.5">{a.detail}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        )}

        {/* Target vs Actual */}
        {view === "target" && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
            <Target size={24} className="text-teal-400 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-900 mb-2">Set Annual Target</p>
            <p className="text-xs text-slate-400 mb-4">
              Set your annual carbon reduction target in the Monthly Inputs page.<br />
              Aligned to SBTi Corporate Net-Zero Standard or your NDC commitment.
            </p>
            <button onClick={() => navigate("/corporate/monthly/inputs")}
              className="px-5 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
              Go to Monthly Inputs →
            </button>
          </div>
        )}

        <p className="text-xs text-slate-400 italic text-center mt-5 pb-2">
          DCI-CORPORATE-PERIODIC-V1.0 · GHG Protocol Corporate Standard + Scope 3 Standard ·
          IFRS S2 aligned · Self-reported data · Platform-verified (not third-party VVB validated)
        </p>
    </div>
  );
}
